import '../css/main.css';
import { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import { NavLink, useLocation } from 'react-router-dom';
import logoPNG from '../assets/brand/logo-transparent.png';

const navLinks = [
  { label: 'Home', to: '/', sectionId: 'hero' },
  { label: 'About', to: '/about', sectionId: 'about' },
  { label: 'Services', to: '/services', sectionId: 'services' },
  { label: 'Projects', to: '/projects', sectionId: 'projects' },
  { label: 'Studio', to: '/gallery', sectionId: 'gallery' },
];

const allSectionIds = ['hero', 'about', 'services', 'projects', 'gallery', 'contact'];

function NavBar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrollSection, setScrollSection] = useState(null);
  const location = useLocation();
  const isHome = location.pathname === '/';

  useEffect(() => {
    if (!isHome) { setScrollSection(null); return; }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) setScrollSection(entry.target.id);
        });
      },
      { rootMargin: '-10% 0px -60% 0px', threshold: 0 }
    );

    allSectionIds.forEach(id => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [isHome]);

  const linkStyle = { fontSize: '11px', letterSpacing: '0.2em' };
  const ctaStyle = { padding: '8px 22px', fontSize: '10px', letterSpacing: '0.15em' };

  return (
    <header className="fixed w-full z-50 bg-black" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
      <div className="relative px-8 md:px-16 flex items-center justify-between" style={{ height: '64px' }}>

        {/* Logo */}
        <NavLink to="/" className="z-10" onClick={() => setMobileOpen(false)}>
          <img src={logoPNG} alt="Edges Design Group" style={{ height: '36px', width: 'auto' }} />
        </NavLink>

        {/* Desktop Nav — truly centered */}
        <nav className="hidden md:flex absolute left-1/2 -translate-x-1/2 items-center" style={{ gap: '40px' }}>
          {navLinks.map(({ label, to, sectionId }) => {
            const scrollActive = isHome && scrollSection === sectionId;
            return (
              <NavLink
                key={label}
                to={to}
                className={({ isActive }) =>
                  `uppercase font-medium transition-all duration-200 ${
                    (scrollActive || isActive) ? 'text-teal-400' : 'hover:text-white'
                  }`
                }
                style={{
                  ...linkStyle,
                  color: undefined,
                }}
              >
                {({ isActive }) => (
                  <span style={{
                    color: (scrollActive || isActive) ? '#2dd4bf' : 'rgba(255,255,255,0.5)',
                    transition: 'color 0.2s',
                  }}>
                    {label}
                  </span>
                )}
              </NavLink>
            );
          })}
        </nav>

        {/* CTA */}
        <div className="hidden md:block z-10">
          <NavLink
            to="/contacts"
            className="uppercase text-white transition-all duration-200"
            style={{
              ...ctaStyle,
              border: '1px solid rgba(255,255,255,0.3)',
              display: 'inline-block',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.borderColor = 'rgba(45,212,191,0.7)';
              e.currentTarget.style.color = '#2dd4bf';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.borderColor = 'rgba(255,255,255,0.3)';
              e.currentTarget.style.color = 'white';
            }}
          >
            Start a Project
          </NavLink>
        </div>

        {/* Mobile toggle */}
        <button
          className="md:hidden p-2 transition"
          style={{ color: 'rgba(255,255,255,0.6)' }}
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Toggle menu"
          onMouseEnter={e => e.currentTarget.style.color = 'white'}
          onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.6)'}
        >
          {mobileOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {/* Mobile Drawer */}
      <div className={`md:hidden overflow-hidden transition-all duration-300 ${
        mobileOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
      }`}>
        <div className="bg-black px-8 py-6 flex flex-col" style={{ gap: '20px', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
          {navLinks.map(({ label, to, sectionId }) => {
            const scrollActive = isHome && scrollSection === sectionId;
            return (
              <NavLink
                key={label}
                to={to}
                className="uppercase font-medium transition-all duration-200"
                style={linkStyle}
                onClick={() => setMobileOpen(false)}
              >
                {({ isActive }) => (
                  <span style={{ color: (scrollActive || isActive) ? '#2dd4bf' : 'rgba(255,255,255,0.5)' }}>
                    {label}
                  </span>
                )}
              </NavLink>
            );
          })}
          <NavLink
            to="/contacts"
            className="uppercase text-white text-center mt-2"
            style={{ ...ctaStyle, border: '1px solid rgba(255,255,255,0.3)' }}
            onClick={() => setMobileOpen(false)}
          >
            Start a Project
          </NavLink>
        </div>
      </div>
    </header>
  );
}

export default NavBar;
